import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  TextField,
  Select,
  Banner,
  BlockStack,
  InlineStack,
  Spinner,
  Modal,
  FormLayout,
  Checkbox,
} from '@shopify/polaris';
import { useApi } from '../contexts/ApiContext.tsx';
import { useToast } from '../contexts/ToastContext.tsx';

interface Brand {
  id: string;
  name: string;
  delay_days: number;
  is_active: boolean;
  product_count: number;
}

export const BrandManagement: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [modalActive, setModalActive] = useState(false);
  const [editForm, setEditForm] = useState({
    delay_days: 0,
    is_active: true,
  });

  const { getBrands, syncBrands, syncMetafields, updateBrandDelay, bulkUpdateBrands } = useApi();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm,
        status: statusFilter === 'all' ? undefined : statusFilter,
      };
      const data = await getBrands(filters);
      setBrands(data.brands || []);
    } catch (error) {
      showError('Failed to load brands');
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncBrands = async () => {
    try {
      setSyncing(true);
      await syncBrands();
      showSuccess('Brands synced successfully');
      loadBrands();
    } catch (error) {
      showError('Failed to sync brands');
      console.error('Error syncing brands:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncMetafields = async () => {
    try {
      setSyncing(true);
      await syncMetafields();
      showSuccess('Metafields synced successfully');
      loadBrands();
    } catch (error) {
      showError('Failed to sync metafields');
      console.error('Error syncing metafields:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleEditBrand = (brand: Brand) => {
    setSelectedBrand(brand);
    setEditForm({
      delay_days: brand.delay_days,
      is_active: brand.is_active,
    });
    setModalActive(true);
  };

  const handleSaveBrand = async () => {
    if (!selectedBrand) return;

    try {
      await updateBrandDelay(selectedBrand.id, editForm);
      showSuccess('Brand updated successfully');
      setModalActive(false);
      loadBrands();
    } catch (error) {
      showError('Failed to update brand');
      console.error('Error updating brand:', error);
    }
  };

  const handleBulkUpdate = async (updates: any[]) => {
    try {
      await bulkUpdateBrands(updates);
      showSuccess('Brands updated successfully');
      loadBrands();
    } catch (error) {
      showError('Failed to update brands');
      console.error('Error updating brands:', error);
    }
  };

  const filteredBrands = brands.filter(brand => {
    const matchesSearch = brand.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && brand.is_active) ||
      (statusFilter === 'inactive' && !brand.is_active);
    return matchesSearch && matchesStatus;
  });

  const rows = filteredBrands.map(brand => [
    brand.name,
    brand.delay_days.toString(),
    brand.is_active ? 'Active' : 'Inactive',
    brand.product_count.toString(),
    <Button size="slim" onClick={() => handleEditBrand(brand)}>
      Edit
    </Button>,
  ]);

  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  return (
    <Page
      title="Brand Management"
      primaryAction={{
        content: 'Sync Brands',
        onAction: handleSyncBrands,
        loading: syncing,
      }}
      secondaryActions={[
        {
          content: 'Sync Metafields',
          onAction: handleSyncMetafields,
          loading: syncing,
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Banner tone="info">
                <p>Manage delivery delays for different brands. Sync brands from your Shopify store and set custom delay periods.</p>
              </Banner>

              <InlineStack gap="400">
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    label="Search brands"
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search by brand name..."
                    autoComplete="off"
                  />
                </div>
                <Select
                  label="Status"
                  options={statusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </InlineStack>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spinner size="large" />
                </div>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'text', 'numeric', 'text']}
                  headings={['Brand Name', 'Delay (Days)', 'Status', 'Products', 'Actions']}
                  rows={rows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={modalActive}
        onClose={() => setModalActive(false)}
        title={`Edit ${selectedBrand?.name}`}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveBrand,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Delay Days"
              type="number"
              value={editForm.delay_days.toString()}
              onChange={(value) => setEditForm({ ...editForm, delay_days: parseInt(value) || 0 })}
              helpText="Number of days to delay delivery for this brand"
              autoComplete="off"
            />
            <Checkbox
              label="Active"
              checked={editForm.is_active}
              onChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
              helpText="Whether this brand delay is currently active"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default BrandManagement;
